import { ValidationValue, ValidationResult } from 'types';

type ValidationFunction = (value: ValidationValue) => ValidationResult;

type ValidationValues<T> = { [K in keyof T]: ValidationValue };
type GenericValidationValues = {
  [key: string]: any;
};

type ValidationSchema<T> = { [K in keyof T]: ValidationFunction | ValidationFunction[] };

type ValidationResults<T> = { [K in keyof T]: ValidationResult };

const isValidationFunctionArray = (
  rule: ValidationFunction | ValidationFunction[],
): rule is ValidationFunction[] => {
  return (rule as ValidationFunction[]) instanceof Array;
};

type Validator<T> = (values: ValidationValues<T>) => ValidationResults<T> | {};

/**
 * Creates a validator that can be used as a custom validator with Formik.
 *
 * NOTE: You should typically prefer Formik's first-class validation with `yup`
 * over this.
 */
export function createValidator<T extends GenericValidationValues>(
  schema: ValidationSchema<T>,
): Validator<T> {
  return (values: ValidationValues<T>): ValidationResults<T> | {} => {
    const initialReduceValue: Partial<ValidationResults<T>> = {};
    const errors = Object.keys(schema).reduce((accum, fieldName: keyof T): ValidationResults<T> => {
      const fieldValue = values[fieldName];
      const fieldValidators = schema[fieldName];
      if (isValidationFunctionArray(fieldValidators)) {
        const ruleIndex = fieldValidators.findIndex(
          (rule: ValidationFunction): boolean => !!rule(fieldValue),
        );
        if (ruleIndex !== -1) {
          accum[fieldName] = fieldValidators[ruleIndex](fieldValue);
        }
      } else {
        // Not sure why type of keyRule is not being narrowed to just ValidationFunction,
        // but it should be?
        accum[fieldName] = (fieldValidators as ValidationFunction)(fieldValue);
      }
      return accum as ValidationResults<T>;
    }, initialReduceValue);
    return Object.values(errors).some((error): boolean => !!error) ? errors : {};
  };
}
